import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { EditOutlined } from '@ant-design/icons';
import {
  toggleSummaryPanel,
  fetchSummaryStart,
  showIntentionFlow,
  getIsLoading,
  getIsExpanded,
  getHasContent,
  getFeatureEnabled,
  getError,
  getDashboardIntention
} from './DashboardSummary.ducks';
import styles from './DashboardSummary.css';

const SummaryButton = ({
  dashboardId,
  activeFilter = null,
  activeProfileId,
  dispatch,
  isLoading,
  isExpanded,
  hasContent,
  featureEnabled,
  error = null,
  dashboardIntention = null
}) => {
  const handleClick = useCallback(() => {
    try {
      // Check if this is the first time (no intention set)
      if (!dashboardIntention) {
        dispatch(showIntentionFlow(false)); // false = not editing, first time
        return;
      }

      // Normal flow: If we don't have content yet and we're not loading, fetch it
      if (!hasContent && !isLoading && dashboardId) {
        dispatch(fetchSummaryStart({ dashboardId, activeFilter, activeProfileId }));
      }
      // Always toggle the panel
      dispatch(toggleSummaryPanel());
    } catch (error) {
      console.error('Error handling summary button click:', error);
    }
  }, [dashboardId, hasContent, isLoading, dispatch, dashboardIntention, activeFilter, activeProfileId]);

  const handleEditIntention = useCallback(() => {
    try {
      dispatch(showIntentionFlow(true)); // true = editing mode
    } catch (error) {
      console.error('Error showing intention edit flow:', error);
    }
  }, [dispatch]);

  const getStatusIndicator = () => {
    if (isLoading) {
      return <span className={classnames(styles.statusIndicator, styles.loading)} />;
    }
    if (error) {
      return <span className={classnames(styles.statusIndicator, styles.error)} />;
    }
    if (hasContent) {
      return <span className={classnames(styles.statusIndicator, styles.success)} />;
    }
    return null;
  };

  if (!featureEnabled) {
    return null;
  }

  const isCommandCenter = document.body.classList.contains('command-center');

  return (
    <div className={styles.summaryButtonContainer}>
      <button
        className={classnames(
          styles.summaryButton,
          {
            [styles.active]: isExpanded,
            [styles.isCommandCenter]: isCommandCenter
          }
        )}
        onClick={handleClick}
        type="button"
        aria-pressed={isExpanded}
      >
        {getStatusIndicator()}
        <span className={styles.summaryButtonText}>Summarize</span>
      </button>

      {dashboardIntention && (
        <button
          className={classnames(
            styles.editIntentionButton,
            { [styles.isCommandCenter]: isCommandCenter }
          )}
          onClick={handleEditIntention}
          type="button"
          title="Edit dashboard intention"
        >
          <EditOutlined />
        </button>
      )}
    </div>
  );
};

SummaryButton.propTypes = {
  dashboardId: PropTypes.string.isRequired,
  activeFilter: PropTypes.object,
  activeProfileId: PropTypes.string,
  // Redux props
  dispatch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  hasContent: PropTypes.bool.isRequired,
  featureEnabled: PropTypes.bool.isRequired,
  error: PropTypes.string,
  dashboardIntention: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

const mapStateToProps = (state) => ({
  isLoading: getIsLoading(state),
  isExpanded: getIsExpanded(state),
  hasContent: getHasContent(state),
  featureEnabled: getFeatureEnabled(state),
  error: getError(state),
  dashboardIntention: getDashboardIntention(state)
});

export default connect(mapStateToProps)(SummaryButton);
